import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAptorApi } from '../../../../../Api';
import { IOnboardingAccount, INamedEntity, PropertyError } from '../../../../../Api/AptorApi';
import { useApiValidationSnackbar } from '../../../../../Api/useValidationSnackbar';
import { Loading } from '../../../../../Components';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useStyles } from './Onboarding.styles';
import { OnboardingReview } from './OnboardingReview';
import { OnboardingConfiguration } from './OnboardingConfiguration';
import { OnboardingDraft, emptyRequirement, lawRows, OnboardingDraftContext, setupPayload, validStep } from './OnboardingDraft';
import { ICustomerUser } from '../../../../Administration/Features/Organization/Users/Users.Form';

const setupSteps = [
  { id: 0, name: 'units' }, { id: 1, name: 'laws' },
  { id: 3, name: 'notes' }, { id: 4, name: 'requirements' },
  { id: 6, name: 'permissions' }, { id: 7, name: 'review' },
];
interface SetupData { account: IOnboardingAccount; languages: INamedEntity[]; areas: INamedEntity[]; }
export const OnboardingWizard = ({ onContentReady }: { onContentReady?: (ready: boolean) => void } = {}) => {
  const [params] = useSearchParams();
  const customerId = Number(params.get('setupCustomerId'));
  const { api, abortController } = useAptorApi();
  const { openOnboarding, userImpersonation, supportInvitation } = useContext(UserContext);
  const { formatMessage: t } = useIntl();
  const { enqueueSnackbar, notifyUnhandledException } = useApiValidationSnackbar();
  const navigate = useNavigate();
  const classes = useStyles();
  const [data, setData] = useState<SetupData>();
  const [draft, setDraft] = useState<OnboardingDraft>();
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [failure, setFailure] = useState('');
  const body = useRef<HTMLDivElement>(null);
  useEffect(() => { if (body.current) body.current.scrollTop = 0; }, [index]);
  useEffect(() => { onContentReady?.(!!data && !!draft && !failure); }, [data, draft, failure, onContentReady]);
  useEffect(() => {
    let active = true;
    setData(undefined); setDraft(undefined); setFailure(''); setIndex(0);
    const load = async () => {
      if (!Number.isSafeInteger(customerId) || customerId <= 0) { setFailure('unavailable'); return; }
      const account = await api.getOnboardingAccount(customerId);
      if (!active) return;
      if (account.completedAtUTC) { navigate('/law-portal/dashboard'); return; }
      await openOnboarding(customerId);
      const [admin, languages, areas] = await Promise.all([
        api.getCustomerUser<ICustomerUser>(account.customerAdminId), api.getLanguages(), api.getCustomerLawSettings('areas'),
      ]);
      if (!active) return;
      setData({ account, languages: languages.items, areas: areas.items });
      setDraft({ units: account.companyUnitDetails, admin: { firstName: admin.firstName, lastName: admin.lastName, email: admin.email, language: admin.language.id },
        selectedLaws: [], unitIds: account.companyUnitDetails.map(unit => unit.id), noteLawIds: [], noteUnitIds: {}, noteCompanyUnitIds: [], noteText: '',
        requirements: [], requirement: emptyRequirement(), unitName: '', notes: {},
      });
    };
    load().catch(error => { if (active) { const status = error?.response?.status; setFailure(status === 401 ? 'session' : status === 403 || status === 404 ? 'unavailable' : 'load-error'); } });
    return () => { active = false; };
  }, [api, customerId, openOnboarding, reload, userImpersonation, supportInvitation?.id, navigate]);
  const update = useCallback((change: (current: OnboardingDraft) => OnboardingDraft) => setDraft(current => current ? change(current) : current), []);
  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    const validation = (errors: PropertyError[]) => errors.forEach(property => property.errors.forEach(error => enqueueSnackbar(error.message, { variant: 'error' })));
    try { await api.invoke(action, abortController.current, validation, notifyUnhandledException); }
    catch { notifyUnhandledException(); }
    finally { setBusy(false); }
  };
  const exit = () => run(async () => {
    try {
      await api.saveOnboardingProgress(customerId, 0);
    } finally {
      try { await openOnboarding(); }
      finally { setDraft(undefined); navigate('/law-portal/dashboard'); }
    }
  });
  if (failure) return <Box className={classes.errorState}>
    <Alert severity="warning">{t({ id: `onboarding.${failure}` })}</Alert>
    <Button onClick={exit} disabled={busy}>{t({ id: 'onboarding.back' })}</Button>
    {failure !== 'unavailable' && <Button onClick={() => setReload(value => value + 1)}>{t({ id: 'onboarding.retry' })}</Button>}
  </Box>;
  if (!data || !draft) return <Loading />;
  const steps = setupSteps;
  const step = steps[index];
  const review = step.id === 7;
  const canContinue = validStep(step.id, draft, data.account);
  const validAll = steps.every(item => validStep(item.id, draft, data.account));
  const preview: IOnboardingAccount = { ...data.account, companyUnitDetails: draft.units, adminCompanyUnits: draft.units,
    customerAdminName: `${draft.admin.firstName} ${draft.admin.lastName}`, customerAdminEmail: draft.admin.email,
    legislation: lawRows(draft),
    otherRequirements: [...(data.account.otherRequirements || []), ...draft.requirements.flatMap((item, position) => item.unitIds.map(id => ({ id: -position - 1, name: item.name, companyUnitName: draft.units.find(unit => unit.id === id)?.name || '' })))],
  };
  const complete = () => run(async () => {
    await api.completeOnboarding(customerId, setupPayload(draft));
    try { await openOnboarding(); } catch { /* Completion is saved; continue to the dashboard if context refresh fails. */ }
    setDraft(undefined);
    navigate('/law-portal/dashboard');
  });
  const next = () => run(async () => {
    await api.saveOnboardingProgress(customerId, index + 1);
    setIndex(value => value + 1);
  });
  const previous = () => run(async () => {
    await api.saveOnboardingProgress(customerId, index);
    setIndex(value => value - 1);
  });
  return <OnboardingDraftContext.Provider value={{ ...data, draft, update }}>
    <Box className={classes.wizard}>
      <Box className={classes.wizardHeader}><Typography variant="h6" component="h2">{t({ id: 'onboarding.setup-title' })}</Typography></Box>
      <Box className={classes.wizardControls}>
        <Box className={classes.toolbar}>
          <Button variant="outlined" color="primary" disabled={busy} onClick={exit}>{t({ id: 'form.cancel' })}</Button>
          <Button variant="outlined" color="primary" disabled={busy || index === 0} onClick={previous}>{t({ id: 'onboarding.previous' })}</Button>
        </Box>
        <Box className={classes.progress} role="progressbar" aria-label={t({ id: `onboarding.step.${step.name}` })} aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={index + 1}>
          <Typography variant="body2">{t({ id: 'law-portal.evalution.plan.step' }, { currentStep: index + 1, totalSteps: steps.length })} — {t({ id: `onboarding.step.${step.name}` })}</Typography>
          <Box className={classes.progressMarkers} aria-hidden="true">{steps.map((item, position) => <Box key={item.id} title={t({ id: `onboarding.step.${item.name}` })} className={`${classes.progressMarker} ${position <= index ? classes.progressMarkerActive : ''}`} />)}</Box>
        </Box>
      </Box>
      <div ref={body} className={classes.wizardBody}><Box className={`${classes.wizardContent} ${!review && step.id === 6 ? classes.adminStepContent : ''}`}>
        {review ? <><OnboardingReview account={preview} /><Alert severity="warning">{t({ id: 'onboarding.complete-help' })}</Alert></> : <OnboardingConfiguration step={step.id} />}
      </Box></div>
      <Box className={classes.wizardFooter}><Button color="primary" variant="contained" disabled={busy || !canContinue || review && !validAll} onClick={() => review ? complete() : next()}>{t({ id: review ? 'onboarding.complete-invite' : 'onboarding.next' })}</Button></Box>
    </Box>
  </OnboardingDraftContext.Provider>;
};
